import React, { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { supabase } from "../../services/supabase";

const BillingPage = () => {
  // Extraemos refreshProfile del contexto
  const { profile, user, refreshProfile } = useAuth();
  const [showIframe, setShowIframe] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const [url, setUrl] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [isCanceling, setIsCanceling] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const isPremium = profile?.is_premium || false;
  const [isSuccess, setIsSuccess] = useState(false);

  const TILOPAY_CHECKOUT_URL = "https://tp.cr/l/TlRBMk1RPT18MQ==";
  const TILOPAY_PORTAL_URL = "https://admin.tilopay.com";

  // --- FUNCIÓN CENTRAL DE ÉXITO ---
  // Esta función actualiza la UI sin recargar la página completa
  const handleUpgradeSuccess = async () => {
    console.log("💎 ¡Suscripción detectada! Actualizando perfil...");
    setShowIframe(false);

    // Llamamos a la nueva función del AuthContext para actualizar los datos globales
    if (refreshProfile) {
      await refreshProfile();
    }
  };

  const handleCancelSubscription = async () => {
    setIsCanceling(true);
    setErrorMessage("");

    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      const response = await fetch(
        "https://jhouwkymemcnoexpulxe.supabase.co/functions/v1/cancelar-suscripcion",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session?.access_token || ""}`,
          },
          body: JSON.stringify({ email: user.email, reason: cancelReason }),
        }
      );

      const result = await response.json();
      if (!response.ok)
        throw new Error(result.error || "No pudimos procesar tu solicitud.");

      // ÉXITO: Mostramos feedback y esperamos antes de cerrar
      setIsSuccess(true);
      setTimeout(async () => {
        setShowCancelModal(false);
        setIsSuccess(false);
        await refreshProfile();
      }, 2000);
    } catch (err) {
      setErrorMessage(err.message);
    } finally {
      setIsCanceling(false);
    }
  };

  // --- LÓGICA DE ACTUALIZACIÓN POR REALTIME ---
  useEffect(() => {
    if (!user?.id) return;

    const channel = supabase
      .channel("schema-db-changes")
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "profiles",
          filter: `id=eq.${user.id}`,
        },
        (payload) => {
          console.log("¡CAMBIO DETECTADO!", payload.new.is_premium);
          // Quitamos el 'if (payload.new.is_premium === true)'
          // Simplemente refrescamos el perfil con lo que venga de la DB
          refreshProfile();

          // Opcional: Cerrar el iframe solo si pasó a true
          if (payload.new.is_premium === true) {
            setShowIframe(false);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id]);

  // --- VERIFICACIÓN MANUAL AL CERRAR ---
  const handleCloseIframe = async () => {
    setShowIframe(false);

    if (user?.id) {
      // Simplemente llamamos a refreshProfile directamente.
      // Esta función ya hace el select a la DB y actualiza el estado global.
      await refreshProfile();
      console.log("Perfil actualizado tras cerrar el portal.");
    }
  };

  const handleStartProcess = () => {
    setShowWarning(true);
  };

  const handleConfirmAndPay = (targetUrl) => {
    setShowWarning(false);
    setIsLoading(true);
    setUrl(targetUrl);
    setShowIframe(true);
  };

  return (
    <div className="max-w-5xl mx-auto p-4 md:p-8 animate-fadeIn relative">
      {/* HEADER */}
      <div className="mb-10">
        <h1 className="text-3xl font-black text-slate-800">Suscripción</h1>
        <p className="text-slate-500">
          Gestiona tu acceso al simulador premium
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-8">
        {/* LADO IZQUIERDO: ESTADO ACTUAL */}
        <div className="bg-white p-6 md:p-8 rounded-3xl border border-slate-200 shadow-sm">
          <h2 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-6">
            Tu Plan Actual
          </h2>
          <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-2xl mb-8">
            <div
              className={`w-14 h-14 rounded-2xl flex items-center justify-center text-3xl ${
                isPremium ? "bg-amber-100" : "bg-blue-100"
              }`}
            >
              {isPremium ? "💎" : "✨"}
            </div>
            <div>
              <p className="text-xl font-black text-slate-800">
                {isPremium ? "Membresía PRO" : "Plan Gratuito"}
              </p>
              <p className="text-sm text-slate-500">
                {isPremium ? "Acceso Total Desbloqueado" : "Acceso Limitado"}
              </p>
            </div>
            {isPremium && (
              <div className="text-right">
                <p className="text-xs font-bold text-slate-400 uppercase">
                  $14.99/mes
                </p>
              </div>
            )}
          </div>
          {isPremium && (
            <div className="mt-6 pt-6 border-t border-slate-100 flex justify-center">
              <button
                onClick={() => setShowCancelModal(true)}
                className="text-xs font-bold text-slate-400 hover:text-red-500 transition-colors uppercase tracking-widest underline decoration-transparent hover:decoration-red-500 underline-offset-4"
              >
                ¿Deseas cancelar tu suscripción?
              </button>
            </div>
          )}
        </div>

        {/* LADO DERECHO: ACCIÓN */}
        {!isPremium && (
          <div className="bg-gradient-to-br from-blue-600 to-indigo-700 p-8 rounded-3xl text-white shadow-xl shadow-blue-200">
            <h3 className="text-2xl font-black mb-4">¿Listo para aprobar?</h3>
            <p className="text-blue-100 mb-8 font-medium">
              Desbloquea todo con el Plan Premium.
            </p>
            <ul className="space-y-4 mb-10">
              {[
                "Simulador Real de examen (100 preguntas)",
                "Simuladores ilimitados",
                "Preguntas de práctica ilimitadas",
                "Explicaciones detalladas 24/7",
                "Acceso en todos tus dispositivos",
                "Biblioteca de leyes completa y actualizada",
                "Análisis de progreso",
                "Soporte prioritario",
              ].map((b, i) => (
                <li
                  key={i}
                  className="flex items-center gap-3 text-sm font-bold"
                >
                  <svg
                    className="w-5 h-5 text-blue-300"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" />
                  </svg>
                  {b}
                </li>
              ))}
            </ul>
            <button
              onClick={handleStartProcess}
              className="w-full py-4 bg-white text-blue-600 rounded-xl font-black text-lg hover:bg-blue-50 transition-all  shadow-lg active:scale-95"
            >
              SUSCRIBIRME POR $14.99 ⚡
            </button>
          </div>
        )}
      </div>

      {/* --- MODAL 1: ADVERTENCIA --- */}
      {showWarning && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-900/80 backdrop-blur-md animate-fadeIn" />
          <div className="relative bg-white w-full max-w-md rounded-3xl p-8 shadow-2xl animate-scaleIn text-center">
            <div className="w-20 h-20 bg-amber-100 text-amber-600 rounded-full flex items-center justify-center text-4xl mx-auto mb-6">
              ⚠️
            </div>
            <h2 className="text-2xl font-black text-slate-800 mb-4">
              Paso Importante
            </h2>
            <p className="text-slate-600 mb-6 leading-relaxed">
              Para activar tu cuenta al instante, asegúrate de escribir este
              correo en el formulario de pago:
            </p>
            <div className="bg-slate-100 p-4 rounded-2xl mb-8 border-2 border-dashed border-slate-300">
              <span className="text-lg font-black text-blue-600 break-all">
                {user?.email}
              </span>
            </div>
            <div className="flex flex-col gap-3">
              <button
                onClick={() => handleConfirmAndPay(TILOPAY_CHECKOUT_URL)}
                className="w-full py-4 bg-blue-600 text-white rounded-2xl font-black hover:bg-blue-700 transition-all"
              >
                ENTENDIDO, IR A PAGAR
              </button>
              <button
                onClick={() => setShowWarning(false)}
                className="w-full py-3 text-slate-400 font-bold hover:text-slate-600 transition-all"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* --- MODAL 2: EL IFRAME --- */}
      {/* --- MODAL 2: EL IFRAME --- */}
      {showIframe && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 md:p-10">
          <div
            className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
            onClick={handleCloseIframe}
          />
          <div className="relative bg-white w-full max-w-4xl h-full max-h-[90vh] rounded-3xl overflow-hidden shadow-2xl flex flex-col animate-scaleIn">
            <div className="bg-white p-4 flex justify-between items-center border-b">
              <div className="flex items-center gap-2">
                {/* Círculo de color dinámico */}
                <div
                  className={`w-2 h-2 rounded-full animate-pulse ${
                    isPremium ? "bg-blue-500" : "bg-green-500"
                  }`}
                />

                {/* Texto dinámico */}
                <span className="text-slate-400 text-[10px] font-black uppercase tracking-widest">
                  {isPremium ? "Portal de Gestión" : "Pago Seguro con Tilopay"}
                </span>
              </div>
              <button
                onClick={handleCloseIframe}
                className="text-slate-400 hover:text-red-500 p-2"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2.5"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Contenido del Iframe */}
            <div className="flex-grow relative bg-slate-50">
              {isLoading && (
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-white z-50">
                  <div
                    className={`w-10 h-10 border-4 border-slate-100 rounded-full animate-spin ${
                      isPremium ? "border-t-blue-600" : "border-t-blue-600"
                    }`}
                  />
                  <span className="mt-2 text-xs font-bold text-slate-400 uppercase tracking-widest">
                    Cargando...
                  </span>
                </div>
              )}
              <iframe
                src={url}
                className="w-full h-full border-none"
                title="Tilopay"
                allow="payment"
                onLoad={() => setIsLoading(false)}
              />
            </div>
          </div>
        </div>
      )}
      {showCancelModal && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
            onClick={() => !isCanceling && setShowCancelModal(false)}
          />
          <div className="relative bg-white w-full max-w-sm rounded-[2rem] p-8 shadow-2xl animate-scaleIn border border-slate-100">
            {isSuccess ? (
              <div className="text-center py-8 animate-fadeIn">
                <div className="w-20 h-20 bg-green-50 text-green-500 rounded-full flex items-center justify-center mx-auto mb-6 text-4xl">
                  ✓
                </div>
                <h3 className="font-black text-slate-800 text-lg mb-2">
                  ¡Baja confirmada!
                </h3>
                <p className="text-sm text-slate-500">
                  Lamentamos verte partir. Estamos procesando tu cambio...
                </p>
              </div>
            ) : (
              <>
                <h2 className="text-2xl font-black text-slate-800 mb-2">
                  ¿Quieres cancelar?
                </h2>
                <p className="text-sm text-slate-500 mb-6">
                  Lamentamos que te vayas. ¿Nos cuentas por qué?
                </p>

                <select
                  className="w-full p-4 bg-slate-50 rounded-2xl border border-slate-200 mb-6 focus:ring-2 focus:ring-red-500 outline-none transition-all font-medium"
                  onChange={(e) => setCancelReason(e.target.value)}
                  disabled={isCanceling}
                >
                  <option value="">Selecciona una razón...</option>
                  <option value="precio">El precio es muy elevado</option>
                  <option value="uso">Ya no utilizo la plataforma</option>
                  <option value="tecnico">Problemas técnicos</option>
                  <option value="otro">Otro motivo</option>
                </select>

                {/* ADVERTENCIA DE IMPACTO */}
                <div className="mb-6 p-4 bg-amber-50 border border-amber-100 rounded-2xl flex gap-3">
                  <span className="text-xl">⚠️</span>
                  <p className="text-xs font-bold text-amber-800 leading-relaxed">
                    Al confirmar, perderás el acceso a los beneficios Premium de
                    forma inmediata.
                  </p>
                </div>

                {errorMessage && (
                  <div className="mb-6 p-4 bg-red-50 border border-red-100 rounded-2xl flex items-start gap-3">
                    <span className="text-lg">❌</span>
                    <p className="text-xs font-bold text-red-600 leading-tight">
                      {errorMessage}
                    </p>
                  </div>
                )}

                <div className="flex flex-col gap-3">
                  <button
                    disabled={!cancelReason || isCanceling}
                    onClick={handleCancelSubscription}
                    className="w-full py-4 bg-slate-900 text-white rounded-2xl font-black hover:bg-slate-800 transition-all disabled:opacity-50 shadow-lg"
                  >
                    {isCanceling ? "Procesando..." : "Confirmar Cancelación"}
                  </button>
                  <button
                    onClick={() => setShowCancelModal(false)}
                    className="w-full py-3 font-bold text-slate-400 hover:text-slate-600 transition-all"
                    disabled={isCanceling}
                  >
                    Mantener mi suscripción
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default BillingPage;
